import { UserContext } from '@vp/ubik-fragment-types'

/**
 * Result of parsing the `forceFeatureGate` query string.
 * - `on`  — gates explicitly forced enabled (`gate` or `gate:true`)
 * - `off` — gates explicitly forced disabled (`gate:false`)
 *
 * Force-off takes precedence over force-on and over the Statsig rollout, so
 * tests (or manual QA) can pin a gate to a known state regardless of the staged
 * rollout percentage. See `useFeatureGate` for the precedence rules.
 */
export interface ForcedFeatureGates {
  on: Set<string>
  off: Set<string>
}

/**
 * Parse the `forceFeatureGate` query string into explicit on/off sets.
 *
 * Supported comma-separated entry forms:
 * - `gate`        → force on
 * - `gate:true`   → force on
 * - `gate:false`  → force off
 *
 * Any suffix other than `false` is treated as force-on, preserving the original
 * `gate`-only behaviour.
 */
export const parseForcedFeatureGates = (raw: string | undefined): ForcedFeatureGates => {
  const on = new Set<string>()
  const off = new Set<string>()

  if (!raw) {
    return { on, off }
  }

  for (const entry of raw.split(',').filter(Boolean)) {
    const [gate, value] = entry.split(':')
    if (!gate) {
      continue
    }
    if (value?.toLowerCase() === 'false') {
      off.add(gate)
    } else {
      on.add(gate)
    }
  }

  return { on, off }
}

export const isFeatureGateEnabled = (featureFlags: UserContext['featureFlags'], gate: string): boolean => {
  return featureFlags?.[gate] === 'true'
}
